















































































































































































































import Vue from 'vue';
import { descending, timeDay, timeFormat, timeHour } from 'd3';
import { ICoupon, IDecoratedSession, IOfferPresented, ISession, ISessionDiscountOffer, IState, SubscriptionInterval } from '@/store/types';
// @ts-ignore
import { ListLoader } from 'vue-content-loader';
import * as actions from '@/store/action-types';
import * as mutations from '@/store/mutation-types';
import DatePicker from '@/components/UI/DatePicker.vue';
import GoToBuilderCard from '@/components/UI/GoToBuilderCard.vue';
import SessionList from '@/components/CancelAnalytics/SessionList.vue';
import { ISankeyNode, ISankeyLink } from '@/components/Vis/D3/Charts/Sankey';
import SankeyWrapper from '@/components/Vis/D3/Charts/SankeyWrapper.vue';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { genBarTimelineSpec } from '@/components/Vis/Vega/Specs/BarTimelineSpec';
import { genHeatmapSpec } from '@/components/Vis/Vega/Specs/HeatmapSpec';
import { genHBarSpec } from '@/components/Vis/Vega/Specs/HBarSpec';
import { TopLevelSpec } from 'vega-lite';
import { discountCouponToString, OFFER_TYPE, SESSION_FETCH_LIMIT } from '@/store/shared';
import { getDateEndpoints } from '@/helpers/date-utils';

const dateTimeFormat = timeFormat('%b %_d');

export default Vue.extend({
  name: 'MainDashboard',
  components: {
    DatePicker,
    SessionList,
    ListLoader,
    VueVegaEmbed,
    SankeyWrapper,
    GoToBuilderCard,
  },
  data(): { loading: boolean; circularCancellationHeatmap: boolean; offerStack: boolean; customerFilterSelection: string } {
    return {
      loading: true,
      circularCancellationHeatmap: false,
      offerStack: true,
      customerFilterSelection: '',
    };
  },
  computed: {
    // indication of what CTA should be shown
    // 'NO_FLOW' | 'NO_SESSIONS' | 'LIVE';
    activationState() {
      const { state } = this.$store;
      if (!(state && state.org)) {
        return null;
      }
      const { blueprints } = state.org;
      if (!(blueprints && blueprints.length)) {
        return 'NO_FLOW';
      }
      if (!this.sessions.length) {
        return 'NO_SESSIONS';
      }
      return 'LIVE';
    },
    dateRange(): [Date, Date] {
      return this.$store.state.dashboardDateRange;
    },
    sessionsExceedLimit(): boolean {
      return this.$store.getters.sessionsExceedLimit;
    },
    couponMap(): { [key: string]: ICoupon } {
      return this.$store.getters.couponMap;
    },
    formattedDateSelection(): string {
      return `from ${dateTimeFormat(this.dateRange[0])} to ${dateTimeFormat(this.dateRange[1])}`;
    },
    // for knowing when to refresh visuals
    selectionKey(): string {
      return this.dateRange[0].toString() + this.dateRange[1].toString() + this.sessions.length;
    },
    sessions(): ISession[] {
      return this.$store.state.sessions;
    },
    selectedSessions(): IDecoratedSession[] {
      return this.$store.getters.selectedSessions;
    },
    filteredSessions(): IDecoratedSession[] {
      if (this.customerFilterSelection) {
        return this.selectedSessions.filter(
          (s) => s.customer?.billingInterval === this.customerFilterSelection || s.customer?.planId === this.customerFilterSelection
        );
      }
      return this.selectedSessions;
    },
    billingIntervals(): SubscriptionInterval[] {
      const set = this.selectedSessions.reduce((acc, sess) => {
        if (sess.customer?.billingInterval) {
          acc.add(sess.customer.billingInterval);
        }
        return acc;
      }, new Set<SubscriptionInterval>());
      return [...set];
    },
    planIds(): string[] {
      const set = this.selectedSessions.reduce((acc, sess) => {
        if (sess.customer?.planId) {
          acc.add(sess.customer.planId);
        }
        return acc;
      }, new Set<string>());
      return [...set];
    },
    visDailySessions(): TopLevelSpec | null {
      if (this.selectedSessions && this.selectedSessions.length) {
        return genBarTimelineSpec({
          data: this.selectedSessions,
          xField: 'createdAt',
          timeUnit: 'yearmonthdate',
        });
      }
      return null;
    },
    sessionCount(): number {
      return (this.selectedSessions || []).length;
    },
    completedSessionCount(): number {
      return (this.selectedSessions || []).reduce((tally: number, session: ISession) => tally + (session.aborted ? 0 : 1), 0);
    },
    cancelCount(): number {
      return (this.selectedSessions || []).reduce((tally: number, session: ISession) => tally + (session.canceled ? 1 : 0), 0);
    },
    offerCount(): number {
      return (this.selectedSessionsFinalAction || []).reduce((tally: number, session: ISession) => tally + (session.acceptedOffer ? 1 : 0), 0);
    },
    sessionCustomers(): number {
      return [...new Set(this.selectedSessions.map((s) => s.customer?.email))].length;
    },
    offerCustomers(): number {
      return [...new Set(this.selectedSessionsFinalAction.filter((s) => s.acceptedOffer).map((s) => s.customer?.email))].length;
    },
    selectedSessionsFinalAction(): IDecoratedSession[] {
      const customerSessionDate = new Map<string, Date>();
      // sorted from most recent to furthest in history
      const chronologicalSessions = [...this.selectedSessions].sort((a, b) => descending(a.createdAt, b.createdAt));
      return chronologicalSessions
        .map((s) => {
          if (s.customer && s.customer.email) {
            // check if its a rapid session situation (aborts then discounts then cancels within 24 hours)
            const recentSession = customerSessionDate.get(s.customer.email);
            if (recentSession !== undefined) {
              const hourDiff = timeHour.count(s.createdAt, recentSession);
              customerSessionDate.set(s.customer.email, s.createdAt);
              return hourDiff > 24 ? s : null;
            }
            customerSessionDate.set(s.customer.email, s.createdAt);
            return s;
          }
          return null;
        })
        .filter((s) => s !== null) as IDecoratedSession[];
    },
    visDailyCancels(): TopLevelSpec | null {
      if (this.selectedSessions && this.selectedSessions.length) {
        return genBarTimelineSpec({
          data: this.selectedSessionsFinalAction.filter((d) => d.canceled),
          xField: 'createdAt',
          timeUnit: 'yearmonthdate',
        });
      }
      return null;
    },
    visDailyOffers(): TopLevelSpec | null {
      if (this.selectedSessions && this.selectedSessions.length) {
        return genBarTimelineSpec({
          data: this.selectedSessionsFinalAction.filter((d) => d.acceptedOffer),
          xField: 'createdAt',
          timeUnit: 'yearmonthdate',
        });
      }
      return null;
    },
    offersPresented(): IOfferPresented[] {
      if (this.loading) {
        return [];
      }
      if (this.filteredSessions) {
        // eslint-disable-next-line arrow-body-style
        return this.filteredSessions.reduce((acc, session) => {
          return acc.concat(
            session.offersPresented
              .filter((o) => o.offer && [OFFER_TYPE.PAUSE, OFFER_TYPE.DISCOUNT].includes(o.offer.offerType))
              .map((o) => ({
                offer: o.offer,
                accepted: o.accepted,
                Offer: this.$store.getters.offerToString(o.offer),
                session,
              }))
          );
        }, [] as IOfferPresented[]);
      }
      return [];
    },
    offerAcceptanceChart(): TopLevelSpec | null {
      if (this.loading) {
        return null;
      }
      if (this.offersPresented && this.offersPresented.length) {
        return genHBarSpec({
          data: this.offersPresented.filter((offer) => this.offerStack || offer.accepted),
          colorField: 'accepted',
          yField: 'Offer',
          stack: this.offerStack ? 'normalize' : null,
          height: 260,
          xAxisTitle: this.offerStack ? 'Offer Acceptance Rate' : 'Offers Accepted',
        });
      }
      return null;
    },
    cancellationReasonHeatmap(): TopLevelSpec | null {
      if (this.loading) {
        return null;
      }
      if (this.filteredSessions && this.filteredSessions.length) {
        const totalResponses = this.selectedSessions.filter((d) => d.surveyResponse).length;
        const responseFrequencies = this.filteredSessions.reduce(
          (counts, session) => {
            if (session.surveyResponse) {
              counts[session.surveyResponse] = counts[session.surveyResponse] ? counts[session.surveyResponse] + 1 : 1;
            }
            return counts;
          },
          {} as { [key: string]: number }
        );
        const responsePct = Object.keys(responseFrequencies).reduce(
          (pcts, response) => {
            pcts[response] = `${((responseFrequencies[response] / totalResponses) * 100).toFixed(0)}%`;
            return pcts;
          },
          {} as { [key: string]: string }
        );
        const heatmapData = this.filteredSessions
          .filter((s) => s.surveyResponse)
          // eslint-disable-next-line arrow-body-style
          .map((session) => {
            return {
              ...session,
              Date: session.createdAt,
              // @ts-ignore
              'Reason for Cancellation': `${session.surveyResponse} (${responsePct[session.surveyResponse]})`,
            };
          });
        return genHeatmapSpec({
          data: heatmapData,
          xField: 'Date',
          yField: 'Reason for Cancellation',
          timeUnit: 'yearmonthdate',
          xLabels: true,
          yLabels: true,
          // yAxisTitle: 'Response',
          circular: this.circularCancellationHeatmap,
          height: 240,
        });
      }
      return null;
    },
    sankey(): { nodes: ISankeyNode[]; links: ISankeyLink[] } {
      const nodes: ISankeyNode[] = [
        { label: 'Started Flow', id: 'initial' },
        { label: 'Started Flow', id: 'entered' },
        { label: 'Retained', id: 'retained' },
      ];

      // const totalEntered = this.selectedSessions.length
      const totalCompleted = this.selectedSessionsFinalAction.filter((s) => s.acceptedOffer || s.canceled).length;
      const totalSaved = this.selectedSessionsFinalAction.filter((s) => s.acceptedOffer).length;
      const totalCanceled = totalCompleted - totalSaved;
      const links: ISankeyLink[] = [
        { source: 'initial', target: 'entered', value: totalCompleted },
        { source: 'entered', target: 'retained', value: totalSaved },
        { source: 'entered', target: 'canceled', value: totalCanceled },
      ];

      const pauses = this.selectedSessionsFinalAction.filter(
        // @ts-ignore
        (s) => s.acceptedOffer && s.acceptedOffer.offerType === OFFER_TYPE.PAUSE && s.acceptedOffer.pauseDuration
      );
      if (pauses.length) {
        nodes.push({ label: 'Pause', id: 'paused' });
        links.push({
          source: 'retained',
          target: 'paused',
          value: pauses.length,
        });
        const pauseDurationCount = pauses.reduce(
          (acc, s) => {
            // @ts-ignore
            const duration = `${s.acceptedOffer.pauseDuration}`;
            acc[duration] = acc[duration] ? acc[duration] + 1 : 1;
            return acc;
          },
          {} as { [key: string]: number }
        );
        const pauseDurations = Object.keys(pauseDurationCount)
          .map((d) => Number.parseInt(d, 10))
          .sort();
        const pauseNodes = pauseDurations.map((duration) => {
          const noun = duration === 1 ? 'month' : 'months';
          return {
            label: `${duration} ${noun}`,
            id: `pause-${duration}`,
          };
        });
        nodes.push(...pauseNodes);
        const pauseLinks = pauseDurations.map((duration) => ({
          source: 'paused',
          target: `pause-${duration}`,
          value: pauseDurationCount[duration],
        }));
        links.push(...pauseLinks);
      }

      const discountSessions = this.selectedSessionsFinalAction.filter((s) => s.acceptedOffer && s.acceptedOffer.offerType === OFFER_TYPE.DISCOUNT);
      if (discountSessions.length) {
        nodes.push({ label: 'Accepted Discount', id: 'discount' });
        links.push({
          source: 'retained',
          target: 'discount',
          value: discountSessions.length,
        });
        const discountCount = discountSessions.reduce(
          (acc, s) => {
            // @ts-ignore
            const { couponId } = s.acceptedOffer;
            acc[couponId] = acc[couponId] ? acc[couponId] + 1 : 1;
            return acc;
          },
          {} as { [key: string]: number }
        );
        const couponIds = Object.keys(discountCount).sort((a, b) => descending(discountCount[a], discountCount[b]));
        const discountNodes: ISankeyNode[] = couponIds.map((couponId) => {
          // @ts-ignore
          const sessionDiscountOffer = discountSessions.find((s) => s.acceptedOffer.couponId === couponId)?.acceptedOffer as ISessionDiscountOffer;
          const label = discountCouponToString({
            id: sessionDiscountOffer.couponId,
            couponAmount: sessionDiscountOffer.couponAmount,
            couponType: sessionDiscountOffer.couponType,
            couponDuration: sessionDiscountOffer.couponDuration,
          });
          return {
            id: `discount-${couponId}`,
            label,
          };
        });
        nodes.push(...discountNodes);

        const discountLinks: ISankeyLink[] = couponIds.map((couponId) => ({
          source: 'discount',
          target: `discount-${couponId}`,
          value: discountCount[couponId],
        }));
        links.push(...discountLinks);
      }

      const contactSessions = this.selectedSessionsFinalAction.filter((s) => s.acceptedOffer && s.acceptedOffer.offerType === OFFER_TYPE.CONTACT);
      if (contactSessions.length) {
        nodes.push({ label: 'Contacted Support', id: 'contact' });
        links.push({
          source: 'retained',
          target: 'contact',
          value: contactSessions.length,
        });
      }

      nodes.push({ label: 'Cancelled', id: 'canceled' });

      return {
        nodes,
        links,
      };
    },
  },
  async created() {
    await this.$store.dispatch(actions.FETCH_ORG_COUPONS);
    await this.$store.dispatch(actions.GET_SESSIONS_AND_BLUEPRINTS, { startDate: this.dateRange[0], endDate: this.dateRange[1] });
    this.checkSessionLimit();
    this.loading = false;
  },
  watch: {
    dateRange(newValue, oldValue): void {
      if (newValue[0] !== oldValue[0] || newValue[1] !== oldValue[1]) {
        this.fetchSessions();
      }
    },
  },
  methods: {
    checkSessionLimit() {
      if (this.sessionsExceedLimit) {
        this.$message({
          showClose: true,
          message: `<p class="text-brand-black font-medium pr-4 pl-1">Selected dates exceed the current limit for the dashboard (${SESSION_FETCH_LIMIT}). Please select a smaller date range</p>`,
          type: 'info',
          duration: 0,
          dangerouslyUseHTMLString: true,
          customClass: 'bg-white rounded-md border-brand-sand shadow-md px-4 py-4',
        });
      }
    },
    handleDateChange(value: Date[]): void {
      this.$store.commit(mutations.SET_DASHBOARD_DATE_RANGE, value);
    },
    async fetchSessions(): Promise<void> {
      this.loading = true;
      await this.$store.dispatch(actions.GET_SESSIONS_AND_BLUEPRINTS, { startDate: this.dateRange[0], endDate: this.dateRange[1] });
      this.checkSessionLimit();
      this.loading = false;
    },
  },
});
